/**
 * Authentication context for the internal testing tool
 *
 * Wraps the Kinde authentication provider and provides a simplified
 * interface for accessing auth state and actions.
 */

import type { ReactNode } from "react";

import { KindeProvider, useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { createContext, useContext, useMemo } from "react";

/**
 * Auth context value interface
 */
export interface AuthContextValue {
	/** Function to get the access token */
	getAccessToken: () => Promise<string | undefined>;
	/** Whether the user is authenticated */
	isAuthenticated: boolean;
	/** Whether auth state is still loading */
	isLoading: boolean;
	/** Function to trigger login */
	login: () => void;
	/** Function to trigger logout */
	logout: () => void;
	/** The authenticated user */
	user: null | {
		email?: string;
		firstName?: string;
		lastName?: string;
		picture?: string;
	};
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Props for the AuthProvider component
 */
interface AuthProviderProps {
	children: ReactNode;
}

/**
 * Authentication provider component
 *
 * Wraps the application with Kinde authentication.
 */
export function AuthProvider({ children }: AuthProviderProps) {
	const audience = import.meta.env.VITE_KINDE_AUDIENCE as string;
	const clientId = import.meta.env.VITE_KINDE_CLIENT_ID as string;
	const domain = import.meta.env.VITE_KINDE_DOMAIN as string;

	// Validate required environment variables
	if (!clientId || !domain) {
		console.error(
			"Missing Kinde configuration. Set VITE_KINDE_CLIENT_ID and VITE_KINDE_DOMAIN.",
		);
		return (
			<div className="flex min-h-screen items-center justify-center bg-gray-50">
				<div className="text-center">
					<h1 className="text-lg font-semibold text-gray-900">
						Configuration Error
					</h1>
					<p className="mt-2 text-sm text-gray-500">
						Authentication is not configured. Please check environment
						variables.
					</p>
				</div>
			</div>
		);
	}

	return (
		<KindeProvider
			audience={audience}
			clientId={clientId}
			domain={domain}
			logoutUri={window.location.origin}
			redirectUri={window.location.origin}
		>
			<AuthProviderInner>{children}</AuthProviderInner>
		</KindeProvider>
	);
}

/**
 * Hook to access the authentication context
 *
 * @returns The auth context value
 * @throws If used outside of AuthProvider
 */
// eslint-disable-next-line react-refresh/only-export-components -- Context files export both provider and hook
export function useAuth(): AuthContextValue {
	const context = useContext(AuthContext);

	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}

	return context;
}

/**
 * Internal component that bridges Kinde auth to our context
 */
function AuthProviderInner({ children }: AuthProviderProps) {
	const { getAccessToken, isAuthenticated, isLoading, login, logout, user } =
		useKindeAuth();

	const value = useMemo<AuthContextValue>(
		() => ({
			getAccessToken: async () => {
				const token = await getAccessToken();
				return token ?? undefined;
			},
			isAuthenticated: isAuthenticated ?? false,
			isLoading: isLoading ?? true,
			login: () => login(),
			logout: () => logout(),
			user: user
				? {
						email: user.email ?? undefined,
						firstName: user.givenName ?? undefined,
						lastName: user.familyName ?? undefined,
						picture: user.picture ?? undefined,
					}
				: null,
		}),
		[isAuthenticated, isLoading, user, getAccessToken, login, logout],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
